import { styled } from '@mui/system'
import { Highlight as HighlightType, TAB_STATUS } from 'entities/Documents'
import { FIELDS as INVOICE_FIELDS } from 'entities/DocumentsInvoiceBase'
import { FocusStateFactory, GeneralOcrState, OcrBuilderState } from 'entities/FocusStateFactory'
import { FIELDS as ORDER_FORM_FIELDS } from 'entities/getOrderFormFields'
import { useGeneralOcrTableContext } from 'hooks/useGeneralOcrTableRefContext'
import { useHighlightContext } from 'hooks/useHighlightContext'
import { useTabContext, useTableContext } from 'hooks/useTabContext'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getHighlightsPattern, splitName, isField } from 'utils/highlights'

const SvgContainer = styled('svg')<{ strokeWidth: number }>(({ strokeWidth }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  overflow: 'visible',
  strokeWidth,
}))

const Highlight = styled('polyline')<{ active: boolean }>(({ active }) => ({
  stroke: active ? '#f57c00' : '#388e3c',
  fill: active ? 'rgb(255,183,77,.2)' : 'rgb(129,199,132,.2)',
}))

type Props = {
  imgSize?: number
  highlights: HighlightType[]
  ocrServiceId: number
}
const Highlights = ({ highlights, imgSize = 0, ocrServiceId }: Props) => {
  const { hlKey, isMatch } = useHighlightContext()
  const fieldRegex = getHighlightsPattern(isMatch, hlKey)
  const state = FocusStateFactory.create(ocrServiceId, useFormContext())
  const generalOcrTableRef = useGeneralOcrTableContext()
  const { handleTabChange } = useTabContext()
  const { handleTableChange } = useTableContext()
  const [targetName, setTargetName] = useState<string>('')

  let strokeWidth = 8
  if (imgSize < 1000) {
    strokeWidth = 2
  } else if (imgSize < 2000) {
    strokeWidth = 4
  }

  function getTabTarget(name: string) {
    const isFieldResult = isField(name)
    if (isFieldResult) {
      return null
    }
    const [category] = splitName(name)
    const isBase =
      Object.keys(INVOICE_FIELDS).includes(category) ||
      Object.keys(ORDER_FORM_FIELDS).includes(category)

    return isBase ? TAB_STATUS.BASE : TAB_STATUS.ITEMS
  }

  function getTableTarget(name: string) {
    const [_, tableIndex] = splitName(name)
    return Number(tableIndex)
  }

  function handleClick(name: string) {
    const tabTarget = getTabTarget(name)
    if (tabTarget === null) {
      return
    }
    if (!(state instanceof OcrBuilderState)) {
      handleTabChange(null, tabTarget)
    }
    if (state instanceof GeneralOcrState) {
      const tableTarget = getTableTarget(name)
      handleTableChange(tableTarget)
    }
    setTargetName(name)
  }

  useEffect(() => {
    if (state instanceof GeneralOcrState) {
      state.setFocusField(targetName, generalOcrTableRef)
    } else {
      state.setFocusField(targetName)
    }
    setTargetName('')
  }, [targetName, state, generalOcrTableRef])

  const highlightComps = highlights.map(({ name, coordinates }) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { top_left, top_right, bottom_right, bottom_left } = coordinates
    const active = Boolean(hlKey) && fieldRegex.test(name)
    return (
      <Highlight
        key={name}
        active={active}
        points={`
          ${top_left[0]},${top_left[1]}
          ${top_right[0]},${top_right[1]}
          ${bottom_right[0]},${bottom_right[1]}
          ${bottom_left[0]},${bottom_left[1]}
          ${top_left[0]},${top_left[1]}
        `}
        onClick={() => handleClick(name)}
      />
    )
  })

  return <SvgContainer strokeWidth={strokeWidth}>{highlightComps}</SvgContainer>
}

export default Highlights
